import { MuiAvatar, MuiTypography } from '@/features/shared/material-ui';
import RichText from '@/features/shared/RichText';
import clsx from 'clsx';
import React, { memo, PropsWithChildren, useMemo } from 'react';
import { AuthorDetailProps, AuthorInfoProps, AuthorProps } from './Author.interface';
import useStyles from './Author.style';

const AuthorDetail = ({
  name,
  image,
  bio,
  classes,
  className: overrideClassName = '',
}: AuthorDetailProps & { classes: any }) => {
  if (!name && !image && !bio) return null;

  return (
    <div
      className={clsx(classes.root, classes.detail, overrideClassName)}
      data-testid="qa-author-detail-component"
    >
      {image?.original?.url && (
        <MuiAvatar
          className={clsx(classes.avatar, 'is-detail')}
          alt={image.altText || ''}
          src={image.original.url}
          data-testid="qa-author-detail-component-avatar"
        />
      )}
      <div>
        {name && (
          <MuiTypography
            variant="subtitle2"
            className={classes.authorName}
            data-testid="qa-author-detail-component-name"
          >
            {name}
          </MuiTypography>
        )}
        {bio && (
          <RichText
            className={classes.bio}
            content={bio}
            data-testid="qa-author-detail-component-bio"
          />
        )}
      </div>
    </div>
  );
};

const AuthorInfo = ({
  name,
  image,
  company,
  classes,
  className: overrideClassName = '',
}: PropsWithChildren<AuthorInfoProps & { classes: any; className: string }>) => {
  if (!name && !image && !company) return null;

  return (
    <div
      className={clsx(classes.root, classes.info, overrideClassName)}
      data-testid="qa-author-info-component"
    >
      {image && (
        <MuiAvatar
          className={clsx(classes.avatar, 'is-info')}
          alt={image.altText || ''}
          src={image.original?.url}
        />
      )}
      <span>
        {name && (
          <MuiTypography
            variant="subtitle2"
            component="span"
            className={classes.authorName}
            data-testid="qa-author-info-component-name"
          >
            {name}
          </MuiTypography>
        )}
        {company && (
          <MuiTypography
            variant="subtitle1"
            component="div"
            className={classes.authorCompany}
            data-testid="qa-author-info-component-company"
          >
            {company}
          </MuiTypography>
        )}
      </span>
    </div>
  );
};

const mapVariantToComponent = (variant: 'detail' | 'info') =>
  variant === 'detail' ? AuthorDetail : AuthorInfo;

const Author = ({
  children,
  author,
  variant = 'info',
  theme,
  className: overrideClassName = '',
}: PropsWithChildren<AuthorProps>) => {
  const classes = useStyles({});
  const Variant = useMemo(() => mapVariantToComponent(variant), [variant]);
  return (
    <Variant
      {...author}
      className={clsx(overrideClassName, `is-${variant}`, `${theme}-theme`)}
      classes={classes}
    >
      {children}
    </Variant>
  );
};

export default memo(Author);
